import {
  Checkbox,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Box,
  Chip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Page from "src/components/Page";
import {
  FormProvider,
  RHFSelect,
  RHFTextField,
} from "src/components/hook-form";
import useSettings from "src/hooks/useSettings";
import { PATH_DASHBOARD } from "src/routes/paths";
import {
  cartAdminAdd,
  masterGetDataByid,
  packageAdminConstituents,
  packageAdminList,
} from "../api";
import RHFAutocomplete from "src/components/hook-form/RHFAutocomplete";
import { useSnackbar } from "notistack";
import { fString } from "src/utils/formatNumber";
import { LoadingButton } from "@mui/lab";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

export function groupBy(array = [], key) {
  return array.reduce((result, currentItem) => {
    const group = currentItem[key];
    if (!result[group]) {
      result[group] = [];
    }

    result[group].push(currentItem);
    return result;
  }, {});
}

const defaultValues = {
  student: "",
  package: "",
  paymentMode: "",
  bankRefNo: "",
};
const schema = Yup.object().shape({
  paymentMode: Yup.string().trim().required("Required"),
  student: Yup.object()
    .shape()
    .nullable()
    .typeError("Required")
    .required("Required"),
  package: Yup.object()
    .shape()
    .nullable()
    .typeError("Required")
    .required("Required"),
  bankRefNo: Yup.string()
    .nullable()
    .notRequired()
    .test(
      "txn-format",
      "Invalid Transaction ID format for selected Payment Mode",
      function (value) {
        if (!value) return true;
        const { paymentMode } = this.parent;

        switch (paymentMode) {
          case "Credit Card":
          case "Debit Card":
          case "Net Banking":
            // alphanumeric + @ . - _ , length 6–20
            return /^[a-zA-Z0-9@._-]{6,20}$/.test(value);

          case "UPI":
            // UPI ka format alag hi rakhte hai
            return /^[a-zA-Z0-9._-]{2,30}@[a-zA-Z]{2,20}$/.test(value);

          default:
            return true;
        }
      }
    ),
});
export default function CreateOrder() {
  const theme = useTheme();
  const { themeStretch } = useSettings();
  const schoolID = JSON.parse(sessionStorage.getItem("schoolId"));
  const { enqueueSnackbar } = useSnackbar();
  const [packageDetail, setPackageDetail] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState([]);
  const [isMendatory, setIsMendatory] = useState(false);
  const [isAllOptional, setIsAllOptional] = useState(false);
  const [loading, setLoading] = useState(false);
  const [optionalQty, setOptionalQty] = useState({});
  const [selectedGroupItem, setSelectedGroupItem] = useState({});
  const [expandedCategory, setExpandedCategory] = useState("all");
  const [groupProduct, setGroupProduct] = useState([]);
  const [selectedProductVariant, setSelectedProductVariant] = useState({});
  const [selectedVariationValueIds, setSelectedVariationValueIds] = useState(
    {}
  );
  const [commentMap, setCommentMap] = useState({});

  const methods = useForm({ defaultValues, resolver: yupResolver(schema) });
  const { handleSubmit, watch, reset, setValue } = methods;

  const studentWatch = watch("student");
  const packageWatch = watch("package");
  const paymentModeWatch = watch("paymentMode");

  const { data: studentList, isLoading } = useQuery({
    queryKey: ["getData", "students", 0, 10000, "schoolID", schoolID],
    queryFn: masterGetDataByid,
    enabled: !!schoolID,
  });
  const { data: packageList, isLoading: isLoadingPackage } = useQuery({
    queryKey: ["packages", studentWatch?.studentID],
    queryFn: packageAdminList,
    enabled: !!studentWatch?.studentID,
  });

  // Helper function to check if category has optional=6 products
  const hasOptional6Products = (categoryName) => {
    return (
      packageDetail?.[categoryName]?.some((item) => item.optional === 6) ||
      false
    );
  };

  // Helper function to get all optional=6 products in a category
  const getOptional6Products = (categoryName) => {
    return (
      packageDetail?.[categoryName]?.filter((item) => item.optional === 6) ||
      []
    );
  };

  // Helper function to check if all optional=6 products in category are selected
  const isAllOptional6Selected = (categoryName) => {
    const optional6Products = getOptional6Products(categoryName);
    if (optional6Products.length === 0) return false;
    return optional6Products.every((product) =>
      selectedPackage.some((sp) => sp.productID === product.productID)
    );
  };

  // Helper function to check if a product is selected
  const isProductSelected = (productId) => {
    return selectedPackage.some((item) => item.productID === productId);
  };

  // Helper function to get item price (uses variant price when selected)
  const getItemPrice = (item) => {
    if (item?.haveVariation === 1 && item?.variations?.length > 0) {
      const variant = selectedProductVariant[item.productID];
      if (variant && variant.price) {
        return parseFloat(variant.price) || 0;
      }
    }
    return parseFloat(item.price) || 0;
  };

  // Helper function to get product quantity
  const getProductQty = (item) => {
    const qty = optionalQty[item.productID];
    if (qty !== undefined && qty !== null && qty !== "") {
      return parseFloat(qty) || 0;
    }

    if (item.optional === 1 || item.optional === 2 || item.optional === 5) {
      return parseFloat(item.qty) || 1;
    }

    if (selectedPackage.some((p) => p.productID === item.productID)) {
      return 1;
    }

    return 0;
  };

  // Helper function to get product total (price × qty)
  const getProductTotal = (item) => {
    const price = getItemPrice(item);
    const qty = getProductQty(item);
    return price * qty;
  };

  const handleVariantChange = (variant, productId) => {
    setSelectedProductVariant((ps) => ({ ...ps, [productId]: variant }));
    setSelectedVariationValueIds((ps) => ({
      ...ps,
      [productId]: variant.variationValueId,
    }));
  };

  const handleCommentChange = (productId, value) => {
    setCommentMap((prev) => ({ ...prev, [productId]: value }));
  };

  const fetchPackageDetails = async (value) => {
    try {
      setLoading(true);
      const data = await packageAdminConstituents(value?.packageID);
      const groupedData = groupBy(data, "categoryName");
      setPackageDetail(groupedData);
      setIsMendatory(
        data.every(
          (el) => el.optional === 1 || el?.optional === 2 || el?.optional === 5
        )
      );
      setIsAllOptional(
        data.every((el) => el.optional === 3 || el?.optional === 4)
      );
      setGroupProduct(data.filter((el) => el.optional === 5));

      // Set default variant (first value) for products with variations
      const initialVariants = {};
      const initialVariationValueIds = {};
      data.forEach((el) => {
        if (el?.haveVariation === 1 && el?.variations?.length > 0) {
          const firstVariant = el?.variations?.[0]?.values?.[0];
          if (firstVariant) {
            initialVariants[el.productID] = firstVariant;
            initialVariationValueIds[el.productID] =
              firstVariant.variationValueId;
          }
        }
      });
      setSelectedProductVariant(initialVariants);
      setSelectedVariationValueIds(initialVariationValueIds);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    if (expandedCategory === "all") {
      setExpandedCategory(category);
      return;
    }
    setExpandedCategory((prev) => (prev === category ? "all" : category));
  };

  const handleCategoryOptional6Change = (e, categoryName) => {
    e.stopPropagation();
    const optional6Products = getOptional6Products(categoryName);
    const isCurrentlySelected = isAllOptional6Selected(categoryName);

    if (isCurrentlySelected) {
      const productIdsToRemove = optional6Products.map((p) => p.productID);
      setSelectedPackage((prev) =>
        prev.filter((item) => !productIdsToRemove.includes(item.productID))
      );
    } else {
      const newProducts = optional6Products.filter(
        (product) =>
          !selectedPackage.some((sp) => sp.productID === product.productID)
      );
      setSelectedPackage((prev) => [...prev, ...newProducts]);

      newProducts.forEach((product) => {
        setOptionalQty((ps) => ({
          ...ps,
          [product.productID]: product.qty || 1,
        }));
      });
    }
  };

  const handleChangeSingle = (el) => {
    const checkIndex = selectedPackage.findIndex(
      (item) => item.productID === el.productID
    );
    if (checkIndex === -1) {
      const newData = [...selectedPackage, el];
      setSelectedPackage(newData);
      setOptionalQty((ps) => ({ ...ps, [el.productID]: el.qty || 1 }));
    } else {
      const filterData = selectedPackage.filter(
        (item) => item.productID !== el.productID
      );
      setSelectedPackage(filterData);
    }
  };

  const handleChangeAll = (e) => {
    if (e.target.checked) {
      const selectedItem = [
        ...selectedPackage,
        ...Object.values(packageDetail)
          ?.flat(1)
          ?.filter((el) => el?.optional === 3 || el?.optional === 4),
      ];
      setSelectedPackage([...new Set(selectedItem)]);
      [...new Set(selectedItem)]?.forEach((el) => {
        setOptionalQty((ps) => ({ ...ps, [el.productID]: el?.qty || 1 }));
      });
      return;
    }
    setSelectedPackage([]);
  };

  const mutation = useMutation((req) => cartAdminAdd(req), {
    onSuccess: (response) => {
      enqueueSnackbar(response?.message || "Order created successfully", {
        variant: "success",
      });
      setOptionalQty({});
      setSelectedPackage([]);
      setSelectedProductVariant({});
      setSelectedVariationValueIds({});
      setCommentMap({});
      reset();
      setPackageDetail(null);
    },
    onError: (error) => {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add order",
        {
          variant: "error",
        }
      );
    },
  });

  const handleGroupSelect = (item) => {
    setSelectedGroupItem((ps) => ({
      ...ps,
      [item.categoryID]: item.productID,
    }));

    // Ensure qty is set (default 1)
    setOptionalQty((prevQty) => ({
      ...prevQty,
      [item.productID]: prevQty[item.productID] || item.qty || 1,
    }));
  };

  const handleChangeQty = (e, item) => {
    const { value } = e.target;
    const numValue = parseInt(value, 10) || 0;

    // Validate against min/max limits
    const minQty = item?.minQty || 1;
    const maxQty = item?.maxQty || item?.qty;

    if (value === "" || (numValue >= minQty && numValue <= maxQty)) {
      setOptionalQty((ps) => ({ ...ps, [item.productID]: value }));
    }
  };

  useEffect(() => {
    Object?.values(packageDetail || {})
      ?.flat(1)
      .forEach((el) => {
        setOptionalQty((ps) => ({
          ...ps,
          [el.productID]: ps[el.productID] || el.qty || 1, // Default qty to 1 if not set
        }));
      });
  }, [packageDetail]);

  // Calculate totals
  const calculateTotals = () => {
    if (!packageDetail) return { GRAND_TOTAL: 0 };

    const allProducts = Object.values(packageDetail).flat(1);

    // 1. Mandatory products (optional=1)
    const mendatoryPrice = allProducts
      .filter((el) => el.optional === 1)
      .reduce((acc, crr) => acc + getProductTotal(crr), 0);

    // 2. Mandatory editable (optional=2)
    const mendatoryEditable = allProducts
      .filter((el) => el.optional === 2)
      .reduce((acc, crr) => acc + getProductTotal(crr), 0);

    // 3. Group Products (optional=5)
    const groupTotal = groupProduct
      .filter((el) => Object.values(selectedGroupItem).includes(el.productID))
      .reduce((acc, crr) => acc + getProductTotal(crr), 0);

    // 4. Optional products (3, 4) - exclude 5 and 6
    const optionalOnlyTotal = selectedPackage
      .filter((el) => el.optional === 3 || el.optional === 4)
      .reduce((acc, crr) => acc + getProductTotal(crr), 0);

    // 5. Grouping Mandatory Products (optional=6)
    const groupingMandatoryTotal = selectedPackage
      .filter((el) => el.optional === 6)
      .reduce((acc, crr) => acc + getProductTotal(crr), 0);

    const optionalTotal =
      optionalOnlyTotal + groupTotal + groupingMandatoryTotal;

    const GRAND_TOTAL = optionalTotal + mendatoryPrice + mendatoryEditable;

    return {
      GRAND_TOTAL,
      mendatoryPrice,
      mendatoryEditable,
      optionalOnlyTotal,
      groupTotal,
      groupingMandatoryTotal,
      optionalTotal,
    };
  };

  const totals = calculateTotals();

  const onSubmit = (data) => {
    const optionalProducts =
      selectedPackage?.map((el) => ({
        productID: el.productID,
        qty: getProductQty(el),
        variationValueId:
          el?.haveVariation === 1
            ? selectedVariationValueIds[el.productID]
            : undefined,
        comment: commentMap[el.productID] || undefined,
      })) || [];

    if (isAllOptional && (!optionalProducts || optionalProducts.length === 0)) {
      enqueueSnackbar("Select a Product", { variant: "error" });
      return;
    }
    // Ensure at least one radio button selection is made
    if (
      groupProduct.length > 0 &&
      Object.keys(selectedGroupItem).length === 0
    ) {
      enqueueSnackbar(
        "Please select at least one product from the mandatory group options before proceeding.",
        {
          variant: "error",
        }
      );
      return;
    }

    // Filter out optional=6 products from optionalProducts array
    const optionalProductsWithout6 = optionalProducts.filter((p) => {
      const product = Object.values(packageDetail || {})
        .flat(1)
        .find((item) => item.productID === p.productID);
      return product?.optional !== 6;
    });

    const filterData = Object?.values(packageDetail || {})
      ?.flat(1)
      .filter((el) => el.optional === 2);

    const mandatoryProducts = filterData.map((el) => ({
      productID: el.productID,
      qty: getProductQty(el),
      variationValueId:
        el?.haveVariation === 1
          ? selectedVariationValueIds[el.productID]
          : undefined,
      comment: commentMap[el.productID] || undefined,
    }));
    const mandatoryGroupProducts = Object.entries(selectedGroupItem).map(
      ([categoryID, productID]) => {
        const product = groupProduct.find((el) => el.productID === productID);
        return {
          productID: productID,
          qty: getProductQty(product),
          variationValueId:
            product?.haveVariation === 1
              ? selectedVariationValueIds[productID]
              : undefined,
          comment: commentMap[productID] || undefined,
        };
      }
    );

    // Add selected optional=6 products to groupingMandatory array
    const groupingMandatory = selectedPackage
      .filter((el) => el.optional === 6)
      .map((el) => ({
        productID: el.productID,
        qty: getProductQty(el),
        variationValueId:
          el?.haveVariation === 1
            ? selectedVariationValueIds[el.productID]
            : undefined,
        comment: commentMap[el.productID] || undefined,
      }));

    const req = {
      paymentMode: data.paymentMode,
      bankRefNo: data.bankRefNo,
      studentID: studentWatch?.studentID,
      packageID: packageWatch.packageID,
      optionalProducts: optionalProductsWithout6,
      mandatoryProducts:
        groupProduct.length > 0
          ? [...mandatoryProducts, ...mandatoryGroupProducts]
          : mandatoryProducts,
      groupingMandatory,
    };
    mutation.mutate({ req });
  };

  // Calculate if checkbox column should be shown
  const hasCheckboxColumn = !isMendatory || groupProduct.length > 0;

  return (
    <Page title="Master">
      <Container maxWidth={themeStretch ? false : "xl"}>
        <HeaderBreadcrumbs
          heading={`Create Order`}
          links={[
            { name: "Dashboard", href: PATH_DASHBOARD.root },
            { name: "Create Order" },
          ]}
        />
        <FormProvider onSubmit={handleSubmit(onSubmit)} methods={methods}>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <RHFAutocomplete
                name={"student"}
                size="small"
                label="Select Student"
                loading={isLoading}
                option={studentList?.data || []}
                getOptionLabel={(option) =>
                  `${option?.firstName} ${option?.lastName} (${option?.studentCode})`
                }
                onInputChange={(value) => {
                  setValue("package", "");
                  setPackageDetail(null);
                }}
                isOptionEqualToValue={(option, value) =>
                  option.studentID === value.studentID
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <RHFAutocomplete
                name={"package"}
                size="small"
                label="Select Package"
                option={packageList || []}
                loading={isLoadingPackage}
                getOptionLabel={(option) => option.packageName}
                onInputChange={(value) => {
                  fetchPackageDetails(value);
                }}
                isOptionEqualToValue={(option, value) =>
                  option.packageID === value.packageID
                }
              />
            </Grid>
          </Grid>
          <div style={{ marginTop: 16 }}>
            {packageDetail && (
              <>
                <TableContainer sx={{ mb: 2 }} component={Paper} elevation={2}>
                  <Table
                    size={"medium"}
                    stickyHeader
                    sx={{
                      "& .MuiTableCell-root": {
                        py: 1,
                        verticalAlign: "top",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {hasCheckboxColumn && (
                          <TableCell sx={{ width: 60 }}>
                            {!isMendatory && (
                              <Checkbox
                                checked={
                                  selectedPackage?.length ===
                                  Object.values(packageDetail)
                                    ?.flat(1)
                                    ?.filter(
                                      (el) =>
                                        el?.optional === 3 || el?.optional === 4
                                    )?.length
                                }
                                onChange={handleChangeAll}
                                indeterminate={
                                  selectedPackage.length > 0 &&
                                  selectedPackage?.length <
                                    Object.values(packageDetail)
                                      ?.flat(1)
                                      ?.filter(
                                        (el) =>
                                          el?.optional === 3 ||
                                          el?.optional === 4
                                      )?.length
                                }
                                size="small"
                              />
                            )}
                          </TableCell>
                        )}

                        <TableCell sx={{ width: 250 }}>PRODUCT</TableCell>
                        <TableCell sx={{ width: 180 }}>AUTHOR / PUB.</TableCell>
                        <TableCell sx={{ width: 150 }}>
                          Single Qty Price
                        </TableCell>
                        <TableCell sx={{ width: 150 }}>Qty</TableCell>
                        <TableCell sx={{ width: 140 }}>Total Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object?.keys(packageDetail)?.map((el, mainIndex) => (
                        <React.Fragment key={el}>
                          {/* Category header row */}
                          <TableRow
                            onClick={() => handleCategoryClick(el)}
                            sx={{
                              cursor: "pointer",
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? theme.palette.grey[800]
                                  : theme.palette.grey[300],
                            }}
                          >
                            {hasCheckboxColumn && (
                              <TableCell
                                sx={{
                                  width: 60,
                                  backgroundColor: "inherit",
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {hasOptional6Products(el) && (
                                  <Checkbox
                                    size="small"
                                    checked={isAllOptional6Selected(el)}
                                    onChange={(e) =>
                                      handleCategoryOptional6Change(e, el)
                                    }
                                    indeterminate={
                                      !isAllOptional6Selected(el) &&
                                      getOptional6Products(el).some((p) =>
                                        isProductSelected(p.productID)
                                      )
                                    }
                                  />
                                )}
                              </TableCell>
                            )}

                            <TableCell
                              colSpan={
                                !isMendatory || groupProduct.length > 0 ? 5 : 6
                              }
                              sx={{ backgroundColor: "inherit" }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                {!isMendatory ||
                                groupProduct.length > 0 ? null : (
                                  <IconButton size="small">
                                    {expandedCategory === "all" ||
                                    expandedCategory === el ? (
                                      <ExpandLess />
                                    ) : (
                                      <ExpandMore />
                                    )}
                                  </IconButton>
                                )}
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? theme.palette.common.white
                                        : theme.palette.common.black,
                                  }}
                                >
                                  {el}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Product rows */}
                          {(expandedCategory === "all" ||
                            expandedCategory === el) &&
                            packageDetail[el].map((item, index) => (
                              <TableRow
                                key={`${el}-${item.productID}-${index}`}
                                sx={{
                                  backgroundColor: (theme) =>
                                    item.optional === 6 &&
                                    !isProductSelected(item.productID)
                                      ? theme.palette.mode === "dark"
                                        ? theme.palette.grey[900]
                                        : theme.palette.grey[100]
                                      : "inherit",
                                  opacity:
                                    item.optional === 6 &&
                                    !isProductSelected(item.productID)
                                      ? 0.5
                                      : 1,
                                }}
                              >
                                {hasCheckboxColumn && (
                                  <TableCell sx={{ width: 60 }}>
                                    {(item.optional === 3 ||
                                      item.optional === 4) && (
                                      <Checkbox
                                        onChange={() =>
                                          handleChangeSingle(item)
                                        }
                                        checked={isProductSelected(
                                          item.productID
                                        )}
                                        size="small"
                                      />
                                    )}

                                    {item.optional === 5 && (
                                      <Radio
                                        checked={
                                          selectedGroupItem[
                                            item?.categoryID
                                          ] === item.productID
                                        }
                                        onChange={() => handleGroupSelect(item)}
                                        value={
                                          selectedGroupItem[item.categoryID]
                                        }
                                        name={`radio-${item.categoryID}`}
                                        size="small"
                                      />
                                    )}
                                  </TableCell>
                                )}

                                <TableCell>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    {item.productName}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontStyle: "italic",
                                      color: "text.secondary",
                                      mt: 0.5,
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {item.description &&
                                    item.description.trim() !== "null" &&
                                    item.description.trim() !== "0"
                                      ? item.description
                                      : "No description available"}
                                  </Typography>

                                  {item.haveVariation === 1 &&
                                    item.variations?.length > 0 && (
                                      <Box sx={{ mt: 1 }}>
                                        {item.variations.map((variant) => (
                                          <Box
                                            key={variant.variationName}
                                            sx={{ mb: 1 }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              {variant?.variationName}:
                                            </Typography>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 0.5,
                                                mt: 0.5,
                                              }}
                                            >
                                              {variant.values.map(
                                                (variantOption) => {
                                                  const isSelected =
                                                    selectedProductVariant?.[
                                                      item.productID
                                                    ]?.variationValueId ===
                                                    variantOption?.variationValueId;

                                                  return (
                                                    <Chip
                                                      key={
                                                        variantOption.variationValueId
                                                      }
                                                      size="small"
                                                      label={`${variantOption.variationValue}: ₹${variantOption.price}`}
                                                      onClick={() =>
                                                        handleVariantChange(
                                                          variantOption,
                                                          item.productID
                                                        )
                                                      }
                                                      color={
                                                        isSelected
                                                          ? "primary"
                                                          : "default"
                                                      }
                                                      variant={
                                                        isSelected
                                                          ? "filled"
                                                          : "outlined"
                                                      }
                                                      sx={{ cursor: "pointer" }}
                                                    />
                                                  );
                                                }
                                              )}
                                            </Box>

                                            {selectedProductVariant[
                                              item.productID
                                            ] && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  display: "block",
                                                  mt: 0.5,
                                                  fontWeight: 600,
                                                  color: "primary.main",
                                                }}
                                              >
                                                Selected:{" "}
                                                {
                                                  selectedProductVariant[
                                                    item.productID
                                                  ]?.variationValue
                                                }{" "}
                                                (₹
                                                {
                                                  selectedProductVariant[
                                                    item.productID
                                                  ]?.price
                                                }
                                                )
                                              </Typography>
                                            )}
                                          </Box>
                                        ))}

                                        <TextField
                                          size="small"
                                          label="Comment (for special size)"
                                          placeholder="e.g. Size XL"
                                          multiline
                                          maxRows={3}
                                          fullWidth
                                          sx={{ mt: 1, maxWidth: 350 }}
                                          value={commentMap[item.productID] || ""}
                                          onChange={(e) =>
                                            handleCommentChange(
                                              item.productID,
                                              e.target.value
                                            )
                                          }
                                          disabled={
                                            item.optional === 6 &&
                                            !isProductSelected(item.productID)
                                          }
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.stopPropagation();
                                            }
                                          }}
                                        />
                                      </Box>
                                    )}
                                </TableCell>

                                <TableCell>
                                  <Typography variant="body2">
                                    {item?.authorName &&
                                    item.authorName.trim() &&
                                    item.authorName !== "null" &&
                                    item.authorName !== "0"
                                      ? item.authorName
                                      : "-"}
                                    /
                                    {item?.publicationName &&
                                    item.publicationName.trim() &&
                                    item.publicationName !== "null" &&
                                    item.publicationName !== "0"
                                      ? item.publicationName
                                      : "-"}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <div className="style-4">
                                    {item.discountPercent > 0 &&
                                      item.discountPercent < 100 && (
                                        <Box
                                          component="span"
                                          sx={{ display: "block" }}
                                        >
                                          <Box
                                            component="del"
                                            sx={{
                                              color: "text.secondary",
                                              fontSize: "0.8rem",
                                            }}
                                          >
                                            ₹
                                            {(
                                              getItemPrice(item) /
                                              (1 - item.discountPercent / 100)
                                            ).toFixed(0)}
                                          </Box>
                                        </Box>
                                      )}

                                    {item.discountPercent > 0 && (
                                      <Typography
                                        variant="caption"
                                        color="success.main"
                                      >
                                        {item.discountPercent}% Discount
                                      </Typography>
                                    )}

                                    <Box
                                      component="ins"
                                      sx={{ textDecoration: "none" }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                      >
                                        ₹{fString(getItemPrice(item))}
                                      </Typography>
                                    </Box>
                                  </div>
                                </TableCell>

                                <TableCell>
                                  {item.optional === 2 ||
                                  item.optional === 4 ||
                                  item.optional === 6 ? (
                                    <TextField
                                      type="number"
                                      size="small"
                                      label="Qty"
                                      sx={{ width: "100%", maxWidth: 120 }}
                                      disabled={
                                        (!isProductSelected(item.productID) &&
                                          item.optional === 4) ||
                                        (item.optional === 6 &&
                                          !isProductSelected(item.productID))
                                      }
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{
                                        min: item?.minQty || 1,
                                        max: item?.maxQty || item?.qty || 999,
                                        maxLength: 3,
                                      }}
                                      value={
                                        optionalQty[item.productID] ||
                                        item.qty ||
                                        1
                                      }
                                      InputProps={{
                                        endAdornment: item.maxQty && (
                                          <InputAdornment position="end">
                                            <Typography variant="caption">
                                              /{item.maxQty}
                                            </Typography>
                                          </InputAdornment>
                                        ),
                                      }}
                                      onChange={(e) => handleChangeQty(e, item)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }
                                      }}
                                      helperText={`Min: ${
                                        item?.minQty || 1
                                      }, Max: ${
                                        item?.maxQty || item?.qty || "∞"
                                      }`}
                                      FormHelperTextProps={{
                                        sx: { fontSize: "0.65rem", mt: 0 },
                                      }}
                                    />
                                  ) : (
                                    <Typography>{item.qty || 1}</Typography>
                                  )}
                                </TableCell>

                                <TableCell>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    ₹{fString(getProductTotal(item))}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        {(!isMendatory || groupProduct.length > 0) && (
                          <TableCell sx={{ width: 60 }} />
                        )}
                        <TableCell colSpan={3} />
                        <TableCell colSpan={2}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center", // <-- vertically center
                              width: "100%",
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>
                              Optional Items:{" "}
                              {selectedPackage?.length +
                                Object.keys(selectedGroupItem).length || 0}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700}>
                              Total: ₹ {fString(totals.GRAND_TOTAL || 0)}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>

                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={3}>
                    <RHFSelect
                      name={"paymentMode"}
                      native={false}
                      size="small"
                      label="Payment Mode"
                      fullWidth
                    >
                      <MenuItem value="">Select Payment</MenuItem>
                      <MenuItem value={"Credit Card"}>Credit Card</MenuItem>
                      <MenuItem value={"Debit Card"}>Debit Card</MenuItem>
                      <MenuItem value={"Net Banking"}>Net Banking</MenuItem>
                      <MenuItem value={"UPI"}>UPI</MenuItem>
                      <MenuItem value={"cash"}>Cash</MenuItem>
                    </RHFSelect>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <RHFTextField
                      label="Transaction ID"
                      name={"bankRefNo"}
                      placeholder="Bank Ref No/Transaction ID"
                      disabled={paymentModeWatch === "cash"}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      loading={mutation.isLoading}
                      type="submit"
                      sx={{ minWidth: 140 }}
                    >
                      Create Order
                    </LoadingButton>
                  </Grid>
                </Grid>
              </>
            )}
            {loading && <LinearProgress />}
          </div>
        </FormProvider>
      </Container>
    </Page>
  );
}
